import { ReactNode } from 'react';
import { Button, Typography } from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { calculateFiltersTotal } from 'utils/Filters';
import { Close } from '@mui/icons-material';

interface FiltersRenderProps<F extends Record<string, any>> {
  value: F;
  initialValues: F;
  setFilter: <K extends keyof F>(filterName: K, newValue?: F[K]) => void;
  setFilters: <K extends keyof F>(filters: Record<K, F[K]>) => void;
}

export interface Props<F extends Record<string, any>> {
  value: F;
  initialValues: F;
  onChange: (value: F) => void;
  onChangeCallback?: () => void;
  showClearButton?: boolean;
  showLabel?: boolean;
  keysToExtractFromTotalCalculation?: (keyof F)[];
  children: (props: FiltersRenderProps<F>) => ReactNode;
}

export const Filters = <F extends Record<string, any>>({
  value,
  initialValues,
  showClearButton,
  showLabel = false,
  keysToExtractFromTotalCalculation,
  onChange,
  onChangeCallback,
  children,
}: Props<F>) => {
  const { t } = useTranslation();

  const totalFilters = calculateFiltersTotal(
    value,
    keysToExtractFromTotalCalculation,
  );

  const setFilters: FiltersRenderProps<F>['setFilters'] = (filters) => {
    onChange({
      ...value,
      ...filters,
    });
    onChangeCallback?.();
  };

  const setFilter: FiltersRenderProps<F>['setFilter'] = (
    filterName,
    newValue,
  ) => {
    onChange({
      ...value,
      [filterName]: newValue,
    });
    onChangeCallback?.();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      columnGap={1}
      rowGap={0.5}
      flexWrap="wrap"
    >
      {showLabel ? (
        <Typography variant="p1" color="textSecondary">
          {`${t('marketplace.filters')}:`}
        </Typography>
      ) : null}

      {children({
        value,
        initialValues,
        setFilter,
        setFilters,
      })}

      {showClearButton ?? !!totalFilters ? (
        <Button
          label={t('table.clear')}
          variant="neutral-fill"
          size="sm"
          icon={Close}
          onClick={() => {
            onChange(initialValues);
            onChangeCallback?.();
          }}
        />
      ) : null}
    </Stack>
  );
};
